/**
 * DDL for sync tables (offline_entities, sync_state, etc.).
 * Run after db.initialize, before schemaRegistry.synchronize.
 */

import { db } from './connection';

const DDL_STATEMENTS = [
  `CREATE TABLE IF NOT EXISTS offline_entities (
    entity_type TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    data TEXT NOT NULL,
    PRIMARY KEY (entity_type, entity_id)
  )`,
  `CREATE TABLE IF NOT EXISTS sync_state (
    phase INTEGER NOT NULL,
    entity_type TEXT NOT NULL,
    last_cursor TEXT,
    last_synced_at TEXT,
    record_count INTEGER DEFAULT 0,
    UNIQUE(phase, entity_type)
  )`,
  `CREATE TABLE IF NOT EXISTS sync_history (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    started_at TEXT NOT NULL,
    finished_at TEXT NOT NULL,
    status TEXT NOT NULL,
    record_counts TEXT,
    error_message TEXT
  )`,
  `CREATE TABLE IF NOT EXISTS synced_customer_codes (
    commercial_code TEXT PRIMARY KEY
  )`,
  `CREATE TABLE IF NOT EXISTS device_info (
    id TEXT PRIMARY KEY,
    model TEXT,
    platform TEXT,
    manufacturer TEXT,
    created_at TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS pending_conflicts (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    entity_type TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    client_data TEXT NOT NULL,
    server_data TEXT NOT NULL,
    resolution TEXT,
    resolved_at TEXT,
    created_at TEXT NOT NULL,
    UNIQUE(entity_type, entity_id)
  )`,
  `CREATE TABLE IF NOT EXISTS sync_queue (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    operation TEXT NOT NULL,
    entity_type TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    payload_json TEXT NOT NULL,
    status TEXT DEFAULT 'pending',
    attempts INTEGER DEFAULT 0,
    max_attempts INTEGER DEFAULT 3,
    error_message TEXT,
    idempotency_key TEXT,
    created_at TEXT NOT NULL,
    processed_at TEXT,
    updated_at TEXT DEFAULT ''
  )`,
  `CREATE TABLE IF NOT EXISTS _schema_version (
    version INTEGER NOT NULL DEFAULT 0
  )`,
  `CREATE TABLE IF NOT EXISTS idempotency_keys (
    key TEXT PRIMARY KEY,
    operation_type TEXT NOT NULL,
    request_hash TEXT NOT NULL,
    local_entity_id TEXT NOT NULL,
    server_entity_id TEXT,
    status TEXT NOT NULL DEFAULT 'pending',
    cached_response TEXT,
    response_status INTEGER,
    error TEXT,
    created_at TEXT NOT NULL,
    updated_at TEXT NOT NULL,
    expires_at TEXT NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_idempotency_local_entity ON idempotency_keys(local_entity_id)`,
  `CREATE INDEX IF NOT EXISTS idx_idempotency_expires ON idempotency_keys(expires_at)`,
  `CREATE INDEX IF NOT EXISTS idx_idempotency_status ON idempotency_keys(status)`,
];

export async function createSyncTables(): Promise<void> {
  const sql = DDL_STATEMENTS.join(';\n');
  await db.execute(sql);
}

let syncSchemaReadyPromise: Promise<void> | null = null;

function isDuplicateColumnError(error: unknown, column: string): boolean {
  const message = (
    error instanceof Error ? error.message : String(error ?? '')
  ).toLowerCase();
  return message.includes(`duplicate column name: ${column.toLowerCase()}`);
}

/**
 * Creates sync tables + runs migrations once per boot wave (dedupes StrictMode / parallel callers).
 */
export async function ensureSyncSchemaReady(): Promise<void> {
  if (syncSchemaReadyPromise) {
    await syncSchemaReadyPromise;
    return;
  }

  const promise = (async () => {
    await createSyncTables();
    await runSyncSchemaMigrations();
  })();

  syncSchemaReadyPromise = promise;
  try {
    await promise;
  } finally {
    if (syncSchemaReadyPromise === promise) {
      syncSchemaReadyPromise = null;
    }
  }
}

/**
 * Migrations: add columns to existing tables if missing.
 * SQLite does not support IF NOT EXISTS for ADD COLUMN.
 * Call after createSyncTables.
 */
export async function runSyncSchemaMigrations(): Promise<void> {
  const sqCols = await db.query<{ name: string }>(
    'PRAGMA table_info(sync_queue)',
  );
  if (sqCols.length > 0 && !sqCols.some((c) => c.name === 'updated_at')) {
    try {
      await db.execute(
        "ALTER TABLE sync_queue ADD COLUMN updated_at TEXT DEFAULT ''",
      );
    } catch (error) {
      // Two boot paths may race and run this migration concurrently.
      // If another caller already added the column, treat it as success.
      if (!isDuplicateColumnError(error, 'updated_at')) {
        throw error;
      }
    }
  }
}

/**
 * Migrations for entity tables. Call after schemaRegistry.synchronize().
 */
export async function runEntityMigrations(): Promise<void> {
  const localOrdersCols = await db.query<{ name: string }>(
    'PRAGMA table_info(local_orders)',
  );
  if (
    localOrdersCols.length > 0 &&
    !localOrdersCols.some((c) => c.name === 'server_id')
  ) {
    try {
      await db.execute('ALTER TABLE local_orders ADD COLUMN server_id TEXT');
    } catch (error) {
      if (!isDuplicateColumnError(error, 'server_id')) {
        throw error;
      }
    }
  }
}
